import { useMemo } from 'react'
import { CaretDownOutlined, CaretUpOutlined, LoadingOutlined } from '@ant-design/icons'
import Skeleton from 'react-loading-skeleton'

import B2becTranslation from '@/components/B2becTranslation'
import FormatPrice from '@/components/FormatPrice'
import FormatStatus from '@/components/FormatStatus'
import { ReorderIcon } from '@/components/Icons'
import usePermission from '@/hooks/usePermission'
import { MODULE_ORDER_RIGHTS, MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { formatDate, getFormatType } from '@/libs/utils/dateUtils'
import { isStringNotEmpty } from '@/libs/utils/stringUltis'

import DownloadButton from '../../components/DownloadButton'
import styles from './OrdersHistoryTable.module.scss'
import RenderCell from './RenderCell'

const useRenderOrderColumns = ({
  isLoading,
  hasActions,
  currentPage,
  reorderIds,
  handleReorder,
  handleAnalyzeLinkName,
  hideStatusColumn,
}) => {
  const {
    verifiedPermissions: [
      hasDownloadInvoicePermission,
      hasReorderPermission,
      canViewCart,
    ],
  } = usePermission([
    MODULE_ORDER_RIGHTS.DOWNLOAD_INVOICE,
    MODULE_ORDER_RIGHTS.REORDER_HISTORIC_ORDER,
    MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_CART,
  ])

  return useMemo(() => {
    let columns = [
      {
        title: ({ sortOrder }) => {
          const ArrowIcon = sortOrder === 'ascend' ? CaretUpOutlined : CaretDownOutlined
          return (
            <span>
              <B2becTranslation value="orders.history.orderDate" />
              <ArrowIcon style={{ marginLeft: 4 }} />
            </span>
          )
        },
        dataIndex: 'orderDate',
        render: (_, { salesOrderNumber, orderDate }) =>
          isLoading ? <Skeleton /> : (
            <RenderCell
              hasActions={hasActions}
              state={{ currentPage }}
              to={`/orders/${salesOrderNumber}`}
              onClick={() => handleAnalyzeLinkName(salesOrderNumber)}
            >
              {formatDate(orderDate, getFormatType().default) || (
                <B2becTranslation value="unavailableDate" />
              )}
            </RenderCell>
          ),
        sorter: (a, b) => new Date(a.orderDate) - new Date(b.orderDate),
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
      {
        title: <B2becTranslation value="orders.history.orderNR" />,
        dataIndex: 'salesOrderNumber',
        render: (text) =>
          isLoading ? <Skeleton /> : (
            <RenderCell
              hasActions={hasActions}
              state={{ currentPage }}
              to={`/orders/${text}`}
              onClick={() => handleAnalyzeLinkName(text)}
            >
              <strong className={styles.saleOrderNumber}>{text}</strong>
            </RenderCell>
          ),
      },
      {
        title: <B2becTranslation value="orders.history.referenceNR" />,
        dataIndex: 'referenceNumber',
        render: (text, { salesOrderNumber }) =>
          isLoading ? <Skeleton /> : (
            <RenderCell
              hasActions={hasActions}
              state={{ currentPage }}
              to={`/orders/${salesOrderNumber}`}
              onClick={() => handleAnalyzeLinkName(salesOrderNumber)}
            >
              <span className={styles.text}>{text}</span>
            </RenderCell>
          ),
      },
      {
        title: <B2becTranslation value="total" />,
        dataIndex: 'totalPrice',
        align: 'right',
        render: (_, { salesOrderNumber, totalPrice, currencyUnit }) => {
          return isLoading ? <Skeleton /> : (
            <RenderCell
              hasActions={hasActions}
              state={{ currentPage }}
              to={`/orders/${salesOrderNumber}`}
              onClick={() => handleAnalyzeLinkName(salesOrderNumber)}
            >
              <FormatPrice
                className={styles.total}
                currencyUnit={currencyUnit}
                price={totalPrice}
                wrappedBy="strong"
              />
            </RenderCell>
          )
        },
      },
    ]

    if (!hideStatusColumn) {
      const statusColumn = {
        title: <B2becTranslation value="status" />,
        dataIndex: 'status',
        render: (text, { salesOrderNumber }) =>
          isLoading ? <Skeleton /> : (
            <RenderCell
              to={`/orders/${salesOrderNumber}`}
              onClick={() => handleAnalyzeLinkName(salesOrderNumber)}
              hasActions={hasActions}
              state={{ currentPage }}
            >
              <FormatStatus className={styles.text} status={text} />
            </RenderCell>
          ),
      }
      const clonedColumnsWithStatus = [
        ...columns.slice(0, 3),
        statusColumn,
        ...columns.slice(3),
      ]

      columns = clonedColumnsWithStatus
    }

    if (hasActions) {
      if (hasDownloadInvoicePermission) {
        columns.push({
          title: <B2becTranslation value="downloadInvoice" />,
          align: 'right',
          render: (
            _,
            {
              salesOrderNumber,
              orderDate,
              referenceNumber,
              status,
              invoicesNumbers,
              showInvoice,
            },
          ) => {
            const shouldShowDownloadButton = !!showInvoice
              && isStringNotEmpty(salesOrderNumber)
              && hasDownloadInvoicePermission

            return isLoading ? <Skeleton /> : (
              shouldShowDownloadButton && (
                <div className={styles.actionWrapper}>
                  <DownloadButton
                    invoiceNumbers={invoicesNumbers}
                    orderDate={orderDate}
                    orderNumber={salesOrderNumber}
                    orderStatus={status}
                    referenceNumber={referenceNumber}
                  />
                </div>
              )
            )
          },
        })
      }

      if (hasReorderPermission) {
        columns.push({
          title: <B2becTranslation value="orders.history.reorder" />,
          dataIndex: 'orderNumber',
          align: 'right',
          width: '10%',
          render: (_, { salesOrderNumber }) => {
            if (!hasReorderPermission) {
              return null
            }

            return isLoading ? <Skeleton /> : (
              <div className={styles.actionWrapper}>
                <button
                  className="button-as-link"
                  type="button"
                  onClick={() => {
                    handleReorder(salesOrderNumber, canViewCart)
                  }}
                >
                  {reorderIds[salesOrderNumber] ? <LoadingOutlined /> : <ReorderIcon />}
                </button>
              </div>
            )
          },
        })
      }
    }

    return columns
  }, [
    currentPage,
    hasActions,
    hasDownloadInvoicePermission,
    hasReorderPermission,
    canViewCart,
    isLoading,
    reorderIds,
    handleAnalyzeLinkName,
    handleReorder,
    hideStatusColumn,
  ])
}

export default useRenderOrderColumns
